// Deterministic CLI surface for the /curate value loop — the agent-mediated
// command layer. NO LLM runs here: the agent (Claude Code / Codex / …) makes
// the judgment (is there something worth capturing? new doc or append?), and
// these subcommands do the deterministic work — list candidates, validate a
// proposal payload, write through the hardened safe-fs gate, and record the
// accept/decline so suppression works.
//
// The contract is a SERIALIZABLE proposal payload (see {@link CuratePayload}):
// the agent constructs it, the user accepts/declines, and only an explicit
// accepted payload reaches the write path. The AI never auto-writes.
//
// Shared with @vortex-os/proactive-curator:
//   - writeDocAction      — the single hardened write gate (path validated,
//                           create = exclusive, append = existing-only).
//   - decline-store        — recordDecline / recordAcceptance / load.
//
// Result objects are plain JSON (the CLI prints them); the agent presents them
// in plain language.

import { existsSync } from "node:fs";
import { createHash } from "node:crypto";
import { readFile, readdir } from "node:fs/promises";
import { join } from "node:path";
import { parseFrontmatter, validateDataRelativePath } from "@vortex-os/core";
import {
  loadDeclinedFingerprints,
  recordAcceptance,
  recordDecline,
  writeDocAction,
  type DocWriteAction,
} from "@vortex-os/proactive-curator";

/** Reserved top-of-`data/` directories the curate loop never proposes into. */
const SYSTEM_META_DIRS = new Set([
  "worklog",
  "decision-log",
  "runbooks",
  "hubs",
  "_memory",
  "_templates",
  "_proactive-curator",
]);

/** Files that are structural, not user documents — excluded from candidates. */
const NON_DOC_FILES = new Set(["README.md", "_INDEX.md", "MEMORY.md"]);

/** The two document actions the v1 value loop supports. */
export type CurateActionKind = "create-file" | "append-section";

/**
 * Serializable accepted-proposal payload — the contract between the agent and
 * the deterministic CLI. The agent builds it (after the user says yes); the
 * accept/preview path validates and acts on it. Plain data only so it can
 * round-trip through a CLI argument or a file.
 *
 *  - `create-file`: needs `filename` (the new file's basename); `targetRelPath`
 *    is the `data/`-relative FOLDER it goes in. `sectionHeader` is unused.
 *  - `append-section`: `targetRelPath` is the `data/`-relative path of the
 *    EXISTING file; `sectionHeader` is the `## ` heading to add. `filename`
 *    is unused.
 *
 * `fingerprint` is the COARSE decline key (see {@link computeCurateFingerprint})
 * — the agent may omit it (the CLI recomputes), but if present it must match.
 */
export interface CuratePayload {
  readonly action: CurateActionKind;
  /** Short topic slug (1-3 words). Part of the coarse fingerprint. */
  readonly topic: string;
  /**
   * `data/`-relative path. For create-file this is the destination FOLDER; for
   * append-section this is the existing FILE. Part of the coarse fingerprint.
   */
  readonly targetRelPath: string;
  /** create-file only: basename of the file to create (e.g. `notes.md`). */
  readonly filename?: string;
  /** append-section only: section heading without the leading `## `. */
  readonly sectionHeader?: string;
  /** Document body to write (create-file) or section body (append-section). */
  readonly body: string;
  /** Optional coarse fingerprint; recomputed + checked if present. */
  readonly fingerprint?: string;
}

export interface CuratePayloadValidation {
  readonly ok: boolean;
  readonly errors: readonly string[];
  /** The concrete `data/`-relative file path the action would touch. */
  readonly effectiveRelPath?: string;
  /** The recomputed coarse fingerprint. */
  readonly fingerprint?: string;
}

/**
 * Coarse decline fingerprint — `sha256(topic + "\n" + actionKind + "\n" +
 * targetRelPath)`, truncated to 16 hex chars. INTENTIONALLY coarse: it does
 * NOT include the body or the section header, so a re-proposal of the same
 * topic+action+target is recognized as the same proposal even if the wording
 * changed. Used identically by accept-record, decline, and the
 * previously-declined check, so a decline actually suppresses re-proposals.
 *
 * `targetRelPath` is normalized to forward slashes so work/home machines agree.
 */
export function computeCurateFingerprint(
  topic: string,
  actionKind: CurateActionKind,
  targetRelPath: string,
): string {
  const normalizedTopic = topic.trim().toLowerCase();
  const normalizedPath = targetRelPath.replace(/\\/g, "/");
  const input = `${normalizedTopic}\n${actionKind}\n${normalizedPath}`;
  return createHash("sha256").update(input).digest("hex").slice(0, 16);
}

/** First path segment of a `data/`-relative path (forward-slash normalized). */
function firstSegment(rel: string): string {
  return rel.replace(/\\/g, "/").replace(/^\/+/, "").split("/")[0] ?? "";
}

function isSystemMetaRel(rel: string): boolean {
  const first = firstSegment(rel);
  return SYSTEM_META_DIRS.has(first) || first.startsWith("_");
}

/**
 * Validate a payload at the CLI boundary (shape + light path sanity). Deeper
 * path safety (traversal/absolute/drive) is enforced authoritatively by
 * {@link writeDocAction} → `validateDataRelativePath` at write time; this layer
 * gives the agent a clear, early, write-nothing report and computes the coarse
 * fingerprint + effective file path.
 */
export function validateCuratePayload(payload: CuratePayload): CuratePayloadValidation {
  const errors: string[] = [];

  if (payload.action !== "create-file" && payload.action !== "append-section") {
    errors.push(`action must be "create-file" or "append-section", got ${JSON.stringify(payload.action)}.`);
    return { ok: false, errors };
  }
  if (typeof payload.topic !== "string" || payload.topic.trim().length === 0) {
    errors.push("topic is required (1-3 word slug).");
  }
  if (typeof payload.targetRelPath !== "string" || payload.targetRelPath.trim().length === 0) {
    errors.push("targetRelPath is required (data-relative).");
  }
  if (typeof payload.body !== "string" || payload.body.length === 0) {
    errors.push("body is required.");
  }

  let effectiveRelPath: string | undefined;
  if (typeof payload.targetRelPath === "string" && payload.targetRelPath.trim().length > 0) {
    if (isSystemMetaRel(payload.targetRelPath)) {
      errors.push(
        `targetRelPath "${payload.targetRelPath}" is inside a reserved system/meta directory — the curate loop writes user documents only.`,
      );
    }
    if (payload.action === "create-file") {
      if (typeof payload.filename !== "string" || payload.filename.trim().length === 0) {
        errors.push("create-file requires a filename (the new file's basename).");
      } else if (payload.filename.includes("/") || payload.filename.includes("\\")) {
        errors.push("filename must be a basename, not a path.");
      } else {
        effectiveRelPath = joinRel(payload.targetRelPath, payload.filename);
      }
    } else {
      // append-section
      if (typeof payload.sectionHeader !== "string" || payload.sectionHeader.trim().length === 0) {
        errors.push("append-section requires a sectionHeader.");
      }
      effectiveRelPath = payload.targetRelPath.replace(/\\/g, "/");
    }
  }

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  const fingerprint = computeCurateFingerprint(
    payload.topic,
    payload.action,
    effectiveRelPath ?? payload.targetRelPath,
  );
  if (payload.fingerprint !== undefined && payload.fingerprint !== fingerprint) {
    return {
      ok: false,
      errors: [
        `Supplied fingerprint ${payload.fingerprint} does not match the recomputed ${fingerprint} ` +
          "(topic/action/target changed?). Drop the fingerprint or rebuild the payload.",
      ],
    };
  }

  return { ok: true, errors: [], effectiveRelPath, fingerprint };
}

function joinRel(...parts: string[]): string {
  return parts
    .filter((p) => p.length > 0)
    .map((p) => p.replace(/\\/g, "/").replace(/^\/+|\/+$/g, ""))
    .join("/");
}

// ─── Subcommand results ──────────────────────────────────────────────────

export interface CurateCandidate {
  /** `data/`-relative path of an existing document. */
  readonly relpath: string;
  /** Frontmatter `topic`, case-folded (lowercased) for matching; or null. */
  readonly topic: string | null;
  /** Frontmatter `tags`, case-folded. */
  readonly tags: readonly string[];
}

export interface CurateCandidatesResult {
  readonly subcommand: "curate-candidates";
  readonly status: "ok";
  readonly candidates: readonly CurateCandidate[];
  /** True when the scan was capped by `maxEntries`. */
  readonly truncated: boolean;
  readonly nextActions: readonly string[];
}

export interface CuratePreviewResult {
  readonly subcommand: "curate-preview";
  readonly status: "ok" | "invalid";
  readonly action?: CurateActionKind;
  readonly effectiveRelPath?: string;
  readonly fingerprint?: string;
  /** What would happen if accepted, in plain words. */
  readonly wouldDo?: string;
  /** True when this exact proposal was previously declined and not expired. */
  readonly previouslyDeclined: boolean;
  /** create-file: does the target already exist (accept would refuse)? */
  readonly targetExists?: boolean;
  readonly errors: readonly string[];
  readonly nextActions: readonly string[];
}

export interface CurateAcceptResult {
  readonly subcommand: "curate-accept";
  readonly status: "written" | "refused" | "invalid";
  readonly action?: CurateActionKind;
  readonly writtenPath?: string;
  readonly fingerprint?: string;
  readonly errors: readonly string[];
  readonly nextActions: readonly string[];
}

export interface CurateDeclineResult {
  readonly subcommand: "curate-decline";
  readonly status: "recorded" | "invalid";
  readonly fingerprint?: string;
  readonly errors: readonly string[];
  readonly nextActions: readonly string[];
}

// ─── curate-candidates ───────────────────────────────────────────────────

/**
 * List existing user documents under `data/` the agent can choose to append
 * to (vs creating a new file). Walks `data/`, skipping reserved system/meta
 * directories and non-document files. Returns relpath + case-folded
 * frontmatter topic + tags so the agent can match the current work's topic to
 * an existing home without an LLM call.
 */
export async function runCurateCandidates(
  repoRoot: string,
  options?: { readonly maxEntries?: number },
): Promise<CurateCandidatesResult> {
  const maxEntries = options?.maxEntries ?? 200;
  const dataDir = join(repoRoot, "data");
  const candidates: CurateCandidate[] = [];
  let truncated = false;

  if (existsSync(dataDir)) {
    async function visit(absDir: string, relDir: string): Promise<void> {
      if (candidates.length >= maxEntries) {
        truncated = true;
        return;
      }
      let entries;
      try {
        entries = await readdir(absDir, { withFileTypes: true });
      } catch {
        return;
      }
      for (const e of entries) {
        if (candidates.length >= maxEntries) {
          truncated = true;
          return;
        }
        if (e.isDirectory()) {
          // Reserve system-meta + any dotfile/_-dir at the root level.
          const atRoot = relDir === "";
          if (e.name.startsWith(".")) continue;
          if (atRoot && (SYSTEM_META_DIRS.has(e.name) || e.name.startsWith("_"))) continue;
          await visit(join(absDir, e.name), joinRel(relDir, e.name));
        } else if (e.isFile() && e.name.endsWith(".md")) {
          if (NON_DOC_FILES.has(e.name)) continue;
          if (e.name.startsWith("_TEMPLATE")) continue;
          let topic: string | null = null;
          let tags: string[] = [];
          try {
            const raw = await readFile(join(absDir, e.name), "utf8");
            const parsed = parseFrontmatter<Record<string, unknown>>(raw);
            if (typeof parsed.frontmatter.topic === "string") {
              topic = parsed.frontmatter.topic.trim().toLowerCase();
            }
            if (Array.isArray(parsed.frontmatter.tags)) {
              tags = parsed.frontmatter.tags
                .filter((t): t is string => typeof t === "string")
                .map((t) => t.trim().toLowerCase());
            }
          } catch {
            // unreadable / no frontmatter — still a candidate, just unenriched
          }
          candidates.push({ relpath: joinRel(relDir, e.name), topic, tags });
        }
      }
    }
    await visit(dataDir, "");
  }

  return {
    subcommand: "curate-candidates",
    status: "ok",
    candidates,
    truncated,
    nextActions: [
      candidates.length === 0
        ? "No existing documents — a capture would be a new file (create-file)."
        : "Match the current topic to a candidate to append; otherwise propose a new file. Then ask the user before calling curate-accept.",
    ],
  };
}

// ─── curate-preview ──────────────────────────────────────────────────────

/**
 * Validate a payload and report what WOULD happen — writes nothing. Reports
 * the previously-declined state (coarse fingerprint, un-expired) so the agent
 * can suppress a re-proposal, and whether a create-file target already exists
 * (accept would refuse to overwrite).
 */
export async function runCuratePreview(
  repoRoot: string,
  payload: CuratePayload,
  now: Date = new Date(),
): Promise<CuratePreviewResult> {
  const v = validateCuratePayload(payload);
  if (!v.ok) {
    return {
      subcommand: "curate-preview",
      status: "invalid",
      previouslyDeclined: false,
      errors: v.errors,
      nextActions: ["Fix the payload and re-run curate-preview."],
    };
  }

  // Layer 2: run the AUTHORITATIVE path-safety gate — the same
  // validateDataRelativePath the accept/write path enforces — so a traversal /
  // absolute / drive-qualified / system-meta path is reported as invalid HERE,
  // not as a deceptively-OK preview that accept would then refuse.
  // validateCuratePayload is intentionally light (shape + system-meta + coarse
  // fingerprint); this closes the gap so preview and accept agree.
  try {
    validateDataRelativePath(join(repoRoot, "data"), v.effectiveRelPath!);
  } catch (e) {
    return {
      subcommand: "curate-preview",
      status: "invalid",
      previouslyDeclined: false,
      errors: [(e as Error).message],
      nextActions: [
        "Fix the path so it stays inside data/ and is not a system/meta directory, then re-run curate-preview.",
      ],
    };
  }

  const declined = await loadDeclinedFingerprints(repoRoot, now);
  const previouslyDeclined = declined.has(v.fingerprint!);

  let targetExists: boolean | undefined;
  let wouldDo: string;
  if (payload.action === "create-file") {
    targetExists = existsSync(join(repoRoot, "data", v.effectiveRelPath!));
    wouldDo = targetExists
      ? `create-file at ${v.effectiveRelPath} — but the file already EXISTS, so accept would REFUSE (no overwrite).`
      : `create a new document at data/${v.effectiveRelPath}.`;
  } else {
    targetExists = existsSync(join(repoRoot, "data", v.effectiveRelPath!));
    wouldDo = targetExists
      ? `append a "## ${payload.sectionHeader}" section to data/${v.effectiveRelPath}.`
      : `append-section to data/${v.effectiveRelPath} — but the file does NOT exist, so accept would FAIL (append-section never creates).`;
  }

  const nextActions: string[] = [];
  if (previouslyDeclined) {
    nextActions.push("This exact proposal was previously declined (not expired) — do NOT re-propose it; stay silent.");
  } else {
    nextActions.push("If the user says yes, call curate-accept with this payload; if no, call curate-decline.");
  }

  return {
    subcommand: "curate-preview",
    status: "ok",
    action: payload.action,
    effectiveRelPath: v.effectiveRelPath,
    fingerprint: v.fingerprint,
    wouldDo,
    previouslyDeclined,
    targetExists,
    errors: [],
    nextActions,
  };
}

// ─── curate-accept ───────────────────────────────────────────────────────

/**
 * Write an accepted proposal through the hardened shared gate, then record the
 * acceptance in the decline-store audit trail. Requires a valid payload — the
 * write path is never reachable without an explicit accepted-proposal payload.
 */
export async function runCurateAccept(
  repoRoot: string,
  payload: CuratePayload,
  now: Date = new Date(),
): Promise<CurateAcceptResult> {
  const v = validateCuratePayload(payload);
  if (!v.ok) {
    return {
      subcommand: "curate-accept",
      status: "invalid",
      errors: v.errors,
      nextActions: ["Fix the payload and retry. The write path requires a valid accepted-proposal payload."],
    };
  }

  const writeAction: DocWriteAction =
    payload.action === "create-file"
      ? { kind: "create-file", targetRelPath: v.effectiveRelPath!, body: payload.body }
      : {
          kind: "append-section",
          targetRelPath: v.effectiveRelPath!,
          sectionHeader: payload.sectionHeader!,
          body: payload.body,
        };

  let writtenPath: string;
  try {
    const res = await writeDocAction(repoRoot, writeAction);
    writtenPath = res.writtenPath;
  } catch (e) {
    // Tailor the advisory hint to the ACTUAL failure, not just the action. Every
    // path-safety rejection from validateDataRelativePath shares the stable
    // "Invalid data-relative path:" prefix; those are NOT the overwrite /
    // missing-file mismatch the action-specific hints describe, so steering the
    // agent to "propose append-section instead" there would misdirect. The
    // authoritative reason is always in errors[]; this only picks the hint.
    const message = (e as Error).message;
    const isPathSafety = message.startsWith("Invalid data-relative path:");
    const hint = isPathSafety
      ? "The path was rejected by the safe-fs gate (see the error). Fix targetRelPath/filename so it stays inside data/ and is not a system/meta directory, then retry."
      : payload.action === "create-file"
        ? "create-file refuses to overwrite. To add to the existing file, propose append-section instead."
        : "append-section appends to an EXISTING file only. To start a new one, propose create-file.";
    return {
      subcommand: "curate-accept",
      status: "refused",
      action: payload.action,
      fingerprint: v.fingerprint,
      errors: [message],
      nextActions: [hint],
    };
  }

  // Audit trail. Uses the same coarse fingerprint as decline so the records
  // line up. kind is "capture-insight" — the curate loop captures insights as
  // documents (the decline-store's two kinds are capture-insight / create-hub).
  await recordAcceptance(repoRoot, {
    kind: "capture-insight",
    fingerprint: v.fingerprint!,
    topic: payload.topic.trim(),
    actionKind: payload.action,
    writtenPath,
    now,
  });

  return {
    subcommand: "curate-accept",
    status: "written",
    action: payload.action,
    writtenPath,
    fingerprint: v.fingerprint,
    errors: [],
    nextActions: [
      payload.action === "append-section"
        ? `Section appended to ${v.effectiveRelPath}. Tell the user where it landed.`
        : `New document at ${v.effectiveRelPath}. Tell the user where it landed.`,
    ],
  };
}

// ─── curate-decline ──────────────────────────────────────────────────────

/**
 * Record a decline against the COARSE fingerprint so the same topic+action+
 * target is suppressed for the expiry window (30 days via decline-store). Body
 * and sectionHeader are not part of the fingerprint, so a re-worded proposal
 * of the same capture stays suppressed.
 */
export async function runCurateDecline(
  repoRoot: string,
  payload: CuratePayload,
  now: Date = new Date(),
): Promise<CurateDeclineResult> {
  const v = validateCuratePayload(payload);
  if (!v.ok) {
    return {
      subcommand: "curate-decline",
      status: "invalid",
      errors: v.errors,
      nextActions: ["Fix the payload and retry."],
    };
  }

  await recordDecline(repoRoot, {
    kind: "capture-insight",
    fingerprint: v.fingerprint!,
    topic: payload.topic.trim(),
    actionKind: payload.action,
    targetPath: v.effectiveRelPath!,
    now,
  });

  return {
    subcommand: "curate-decline",
    status: "recorded",
    fingerprint: v.fingerprint,
    errors: [],
    nextActions: ["Declined. This proposal will not re-surface for 30 days. Stay silent on it."],
  };
}
